import { createEntityAdapter, EntityState } from '@ngrx/entity';
import { createFeature, createReducer, on } from '@ngrx/store';
import { CryptoSettings, PortfolioPerformance, Position, PositionsSettings } from '@zero/shared/util-models';
import { getErrorMessage } from '../base/effect-helpers';
import { positionsActions, positionsApiActions } from './positions.actions';

export const positionsAdapter = createEntityAdapter<Position>({
  selectId: (position) => position.isin,
});

export interface PositionsState extends EntityState<Position> {
  stockISINs: string[];
  positionsLoading: boolean;
  positionsInitialLoaded: boolean;
  performance: PortfolioPerformance | null;
  performanceCalculated: boolean;
  notesLoading: boolean;
  notesInitialLoaded: boolean;
  positionSettings: PositionsSettings | null;
  cryptoSettings: CryptoSettings | null;
  positionsLoadingError: string | null;
  categories: string[];
}

export const initialState: PositionsState = positionsAdapter.getInitialState({
  stockISINs: [],
  positionsLoading: false,
  positionsInitialLoaded: false,
  performance: null,
  performanceCalculated: false,
  notesLoading: false,
  notesInitialLoaded: false,
  positionSettings: null,
  cryptoSettings: null,
  positionsLoadingError: null,
  categories: [],
});

export const positionsFeature = createFeature({
  name: 'positions',
  reducer: createReducer(
    initialState,
    on(
      positionsApiActions.loadPositionsSuccess,
      (state, { positions }): PositionsState => ({
        ...positionsAdapter.setAll(positions, state),
        positionsLoading: false,
        positionsInitialLoaded: true,
      }),
    ),
    on(
      positionsApiActions.loadPositionsFailure,
      (state, { error }): PositionsState => ({
        ...state,
        positionsLoadingError: getErrorMessage(error),
        positionsLoading: false,
      }),
    ),
    on(
      positionsActions.calculatePortfolioPerformance,
      (state, { performance, quotesLoaded }): PositionsState => ({
        ...state,
        performance,
        performanceCalculated: quotesLoaded,
      }),
    ),
    on(
      positionsActions.positionsPerformanceCalculated,
      (state, { positions }): PositionsState => ({
        ...positionsAdapter.updateMany(
          positions.map((position) => ({
            id: position.isin,
            changes: position,
          })),
          state,
        ),
        performanceCalculated: true,
      }),
    ),
    on(
      positionsActions.calculatePortfolioPerformanceFailure,
      (state): PositionsState => ({
        ...state,
        performanceCalculated: false,
      }),
    ),
    on(
      positionsActions.setLoading,
      (state, { loading }): PositionsState => ({
        ...state,
        positionsLoading: loading,
      }),
    ),
    on(
      positionsActions.loadPositionsNotes,
      (state): PositionsState => ({
        ...state,
        notesLoading: true,
      }),
    ),
    on(
      positionsApiActions.loadPositionsNotesSuccess,
      (state, { notes }): PositionsState => ({
        ...positionsAdapter.updateMany(
          notes.map((note) => ({
            id: note.isin,
            changes: {
              note,
            },
          })),
          state,
        ),
        notesLoading: false,
      }),
    ),
    on(
      positionsApiActions.loadPositionsNotesFailure,
      (state): PositionsState => ({
        ...state,
        notesLoading: false,
      }),
    ),
    on(
      positionsActions.loadPositionNoteByIsin,
      (state): PositionsState => ({
        ...state,
        notesLoading: true,
      }),
    ),
    on(
      positionsApiActions.loadPositionNoteByIsinSuccess,
      (state, { note }): PositionsState => ({
        ...positionsAdapter.updateOne(
          {
            id: note.isin,
            changes: {
              note,
            },
          },
          state,
        ),
        notesLoading: false,
      }),
    ),
    on(
      positionsApiActions.loadPositionNoteByIsinFailure,
      (state): PositionsState => ({
        ...state,
        notesLoading: false,
      }),
    ),
    on(positionsApiActions.updatePositionNoteSuccess, (state, { note }) => {
      return positionsAdapter.updateOne(
        {
          id: note.isin,
          changes: {
            note,
          },
        },
        state,
      );
    }),
    on(positionsApiActions.updatePositionEntryQuoteSuccess, (state, { position }) => {
      const { perfPct, perfAbs, perfTodayAbs, perfTodayPct, entryValue, currentValue, isin, avgEntryQuote } = position;
      return positionsAdapter.updateOne(
        {
          id: isin,
          changes: {
            perfPct,
            perfAbs,
            perfTodayAbs,
            perfTodayPct,
            entryValue,
            currentValue,
            avgEntryQuote,
          },
        },
        state,
      );
    }),
    on(
      positionsActions.setPositionCategories,
      (state, { categories }): PositionsState => ({
        ...state,
        categories,
      }),
    ),
  ),
  extraSelectors: ({ selectPositionsState }) => ({
    ...positionsAdapter.getSelectors(selectPositionsState),
  }),
});
